// CharacterRatingModal.tsx - Enhanced with search and identity concerns
import React, { useState, useEffect } from 'react';

const BACKEND_URL = '';

interface RaidCharacter {
  id: string;
  name: string;
  class_name: string;
  class_key: string;
  role_label: string;
  is_raid_leader: boolean;
  is_loot_master: boolean;
  is_me: boolean;
  existing_rating_id: string | null;
  conduct_rating: number | null;
  skill_rating: number | null;
  timeliness_rating: number | null;
  preparation_rating: number | null;
  identity_concern: boolean | null;
  identity_concern_details: string | null;
  notes: string | null;
  evidence_urls: string[] | null;
  // Summary stats
  total_ratings: number | null;
  avg_conduct: number | null;
  avg_skill: number | null;
  avg_timeliness: number | null;
  avg_preparation: number | null;
  overall_rating: number | null;
  identity_concerns_count: number | null;
}

const RATING_EMOJIS: { [key: number]: string } = {
  1: '😠', // Terrible
  2: '😟', // Poor
  3: '😐', // Okay
  4: '🙂', // Good
  5: '😊', // Excellent
};

const RATING_LABELS: { [key: number]: string } = {
  1: '😠', // Terrible
  2: '😟', // Poor
  3: '😐', // Okay
  4: '🙂', // Good
  5: '😊', // Excellent
};

// WoW Class Colors
const CLASS_COLORS: Record<string, string> = {
  'warrior': '#C79C6E',
  'paladin': '#F58CBA',
  'hunter': '#ABD473',
  'rogue': '#FFF569',
  'priest': '#FFFFFF',
  'death knight': '#C41F3B',
  'shaman': '#0070DE',
  'mage': '#69CCF0',
  'warlock': '#9482C9',
  'monk': '#00FF96',
  'druid': '#FF7D0A',
  'demon hunter': '#A330C9',
};

export default function CharacterRatingModal({ 
  raidId, 
  raidName,
  myCharacterId,
  onClose 
}: { 
  raidId: string;
  raidName: string;
  myCharacterId: string;
  onClose: () => void;
}) {
  const [characters, setCharacters] = useState<RaidCharacter[]>([]);
  const [filteredCharacters, setFilteredCharacters] = useState<RaidCharacter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChar, setSelectedChar] = useState<RaidCharacter | null>(null);
  const [ratings, setRatings] = useState({
    conduct: null as number | null,
    skill: null as number | null,
    timeliness: null as number | null,
    preparation: null as number | null,
  });
  const [identityConcern, setIdentityConcern] = useState(false);
  const [identityDetails, setIdentityDetails] = useState('');
  const [notes, setNotes] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch characters
  useEffect(() => {
    fetch(`${BACKEND_URL}/character-ratings/raid/${raidId}/characters`, {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(data => {
        const filtered = data.filter((c: RaidCharacter) => !c.is_me);
        setCharacters(filtered);
        setFilteredCharacters(filtered);
        setLoading(false);
      })
      .catch(console.error);
  }, [raidId]);

  // Filter characters by search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCharacters(characters);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredCharacters(
        characters.filter(c => 
          c.name.toLowerCase().includes(query) ||
          c.class_name.toLowerCase().includes(query) ||
          c.role_label.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, characters]);

  const handleSelectCharacter = (char: RaidCharacter) => {
    setSelectedChar(char);
    // Load existing ratings if any
    setRatings({
      conduct: char.conduct_rating,
      skill: char.skill_rating,
      timeliness: char.timeliness_rating,
      preparation: char.preparation_rating,
    });
    setIdentityConcern(char.identity_concern || false);
    setIdentityDetails(char.identity_concern_details || '');
    setNotes(char.notes || '');
  };

  const handleSubmit = async () => {
    if (!selectedChar) return;

    setSaving(true);
    try {
      // TODO: Upload images first if any, get URLs
      const evidence_urls: string[] = [];

      const response = await fetch(`${BACKEND_URL}/character-ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          raid_id: raidId,
          rated_character_id: selectedChar.id,
          rater_character_id: myCharacterId,
          conduct_rating: ratings.conduct,
          skill_rating: ratings.skill,
          timeliness_rating: ratings.timeliness,
          preparation_rating: ratings.preparation,
          identity_concern: identityConcern,
          identity_concern_details: identityConcern ? identityDetails.trim() || null : null,
          notes: notes.trim() || null,
          evidence_urls
        })
      });

      if (response.ok) {
        // Update local state
        setCharacters(prev => prev.map(c => 
          c.id === selectedChar.id 
            ? { 
                ...c, 
                ...ratings, 
                identity_concern: identityConcern,
                identity_concern_details: identityDetails,
                notes,
                existing_rating_id: 'updated' 
              }
            : c
        ));
        setSelectedChar(null);
        setRatings({ conduct: null, skill: null, timeliness: null, preparation: null });
        setIdentityConcern(false);
        setIdentityDetails('');
        setNotes('');
      }
    } catch (error) {
      console.error('Failed to save rating:', error);
    } finally {
      setSaving(false);
    }
  };

  const getClassColor = (classKey: string) => {
    return CLASS_COLORS[classKey?.toLowerCase()] || '#6366F1';
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--c-surface)] rounded-xl border border-[var(--c-line)] max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--c-line)] flex items-center justify-between bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
          <div>
            <h2 className="text-xl font-bold text-[var(--c-ink)]">Rate Players</h2>
            <p className="text-sm text-[var(--c-ink-2)]">{raidName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-[var(--c-sunken-2)] flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5 text-[var(--c-ink-2)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex h-[calc(90vh-5rem)]">
          
          {/* Left: Character List */}
          <div className="w-96 border-r border-[var(--c-line)] overflow-hidden flex flex-col bg-[var(--c-surface)]">
            
            {/* Search */}
            <div className="p-4 border-b border-[var(--c-line)]">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search characters..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-cyan-500/50 focus:bg-[var(--c-sunken-2)]"
                />
              </div>
            </div>

            {/* Character List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading ? (
                <div className="text-[var(--c-ink-3)] text-sm text-center py-8">Loading...</div>
              ) : filteredCharacters.length === 0 ? (
                <div className="text-[var(--c-ink-3)] text-sm text-center py-8">
                  {searchQuery ? 'No characters found' : 'No other players in raid'}
                </div>
              ) : (
                filteredCharacters.map(char => (
                  <button
                    key={char.id}
                    onClick={() => handleSelectCharacter(char)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedChar?.id === char.id
                        ? 'bg-cyan-500/20 border-cyan-500/50 shadow-lg shadow-cyan-500/20'
                        : 'bg-[var(--c-sunken)] border-[var(--c-line)] hover:bg-[var(--c-sunken-2)] hover:border-[var(--c-line-strong)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {/* Class Icon/Avatar */}
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-[var(--c-ink)] shadow-inner"
                        style={{ backgroundColor: getClassColor(char.class_key) }}
                      >
                        {char.name.substring(0, 2).toUpperCase()}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span 
                            className="font-bold text-sm truncate"
                            style={{ color: getClassColor(char.class_key) }}
                          >
                            {char.name}
                          </span>
                          {char.existing_rating_id && (
                            <span className="text-xs text-cyan-700 dark:text-cyan-400 flex-shrink-0">&#10003;</span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--c-ink-2)] truncate">
                          {char.class_name} &middot; {char.role_label}
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        {char.is_raid_leader && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[9px] font-bold">RL</span>
                        )}
                        {char.is_loot_master && (
                          <span className="px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-700 dark:text-purple-300 text-[9px] font-bold">ML</span>
                        )}
                      </div>
                    </div>

                    {/* Rating Summary */}
                    {char.total_ratings && char.total_ratings > 0 && (
                      <div className="flex items-center gap-2 pt-2 border-t border-[var(--c-line-soft)]">
                        <div className="text-xs text-[var(--c-ink-3)]">
                          {Number(char.overall_rating || 0).toFixed(1)}/5.0
                        </div>
                        <div className="flex-1 h-1 bg-[var(--c-sunken-2)] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
                            style={{ width: `${Number(char.overall_rating || 0) * 20}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-[var(--c-ink-3)]">
                          {char.total_ratings} rating{char.total_ratings !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}

                    {/* Warning for identity concerns */}
                    {char.identity_concerns_count && char.identity_concerns_count > 0 && (
                      <div className="mt-2 px-2 py-1 rounded bg-red-500/20 border border-red-500/30">
                        <div className="text-[10px] text-red-700 dark:text-red-300 font-medium">
                          ⚠️  {char.identity_concerns_count} identity concern{char.identity_concerns_count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right: Rating Form */}
          <div className="flex-1 overflow-y-auto p-6">
            {!selectedChar ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-[var(--c-sunken)] flex items-center justify-center">
                  <svg className="w-8 h-8 text-[var(--c-ink-3)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-[var(--c-ink-2)] text-sm font-medium mb-1">Select a player to rate</div>
                  <div className="text-[var(--c-ink-3)] text-xs">Choose from the list on the left</div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                
                {/* Selected Character */}
                <div className="bg-[var(--c-sunken)] rounded-lg p-4 border border-[var(--c-line)]">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-[var(--c-ink)] shadow-inner"
                      style={{ backgroundColor: getClassColor(selectedChar.class_key) }}
                    >
                      {selectedChar.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div 
                        className="font-bold text-[var(--c-ink)] text-lg"
                        style={{ color: getClassColor(selectedChar.class_key) }}
                      >
                        {selectedChar.name}
                      </div>
                      <div className="text-sm text-[var(--c-ink-2)]">{selectedChar.class_name} &middot; {selectedChar.role_label}</div>
                    </div>
                  </div>
                </div>

                {/* Ratings */}
                {(['conduct', 'skill', 'timeliness', 'preparation'] as const).map(category => (
                  <div key={category}>
                    <label className="block text-sm font-medium text-[var(--c-ink)] mb-2 capitalize">
                      {category}
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(value => (
                        <button
                          key={value}
                          onClick={() => setRatings(prev => ({ ...prev, [category]: value }))}
                          title={RATING_LABELS[value]}
                          className={`flex-1 h-16 rounded-lg border text-3xl transition-all ${
                            ratings[category] === value
                              ? 'bg-cyan-500/30 border-cyan-500/60 scale-105 shadow-lg shadow-cyan-500/20'
                              : 'bg-[var(--c-sunken)] border-[var(--c-line)] hover:bg-[var(--c-sunken-2)] hover:border-[var(--c-line-strong)] hover:scale-105'
                          }`}
                        >
                          {RATING_EMOJIS[value]}
                        </button>
                      ))}
                    </div>
                    {ratings[category] && (
                      <div className="mt-1 text-xs text-center text-[var(--c-ink-3)]">
                        {RATING_LABELS[ratings[category]!]}
                      </div>
                    )}
                  </div>
                ))}

                {/* Identity Concern */}
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={identityConcern}
                      onChange={e => setIdentityConcern(e.target.checked)}
                      className="w-5 h-5 rounded border-2 border-red-500/50 bg-[var(--c-sunken)] checked:bg-red-500 checked:border-red-500 transition-colors cursor-pointer"
                    />
                    <div>
                      <div className="text-sm font-medium text-red-700 dark:text-red-300">Identity Concern</div>
                      <div className="text-xs text-red-700/70 dark:text-red-300/70">This player may not be who they claim to be</div>
                    </div>
                  </label>

                  {identityConcern && (
                    <textarea
                      value={identityDetails}
                      onChange={e => setIdentityDetails(e.target.value)}
                      placeholder="Explain the identity concern (e.g., different voice, behavior, skill level mismatch, etc.)"
                      maxLength={500}
                      rows={3}
                      className="mt-3 w-full rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-red-500/50 focus:bg-red-500/10 resize-none"
                    />
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-[var(--c-ink)] mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Describe any specific issues, incidents, or commendations..."
                    maxLength={2000}
                    rows={4}
                    className="w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-4 py-3 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-3)] outline-none focus:border-cyan-500/50 focus:bg-[var(--c-sunken-2)] focus:ring-2 focus:ring-cyan-500/20 resize-none"
                  />
                  <div className="mt-1 text-xs text-[var(--c-ink-3)] text-right">{notes.length}/2000</div>
                </div>

                {/* Evidence Upload */}
                <div>
                  <label className="block text-sm font-medium text-[var(--c-ink)] mb-2">
                    Evidence (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={e => setImages(Array.from(e.target.files || []))}
                    className="block w-full text-sm text-[var(--c-ink-2)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-cyan-500/20 file:text-cyan-200 hover:file:bg-cyan-500/30 file:cursor-pointer"
                  />
                  {images.length > 0 && (
                    <div className="mt-2 text-xs text-cyan-700 dark:text-cyan-400">{images.length} image(s) selected</div>
                  )}
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4 border-t border-[var(--c-line)]">
                  <button
                    onClick={() => {
                      setSelectedChar(null);
                      setRatings({ conduct: null, skill: null, timeliness: null, preparation: null });
                      setIdentityConcern(false);
                      setIdentityDetails('');
                      setNotes('');
                      setImages([]);
                    }}
                    className="flex-1 px-4 py-3 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] text-[var(--c-ink-2)] text-sm font-medium hover:bg-[var(--c-sunken-2)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={saving || (!ratings.conduct && !ratings.skill && !ratings.timeliness && !ratings.preparation && !identityConcern)}
                    className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-cyan-500/30 to-cyan-600/30 border border-cyan-500/50 text-cyan-800 dark:text-cyan-100 text-sm font-bold hover:from-cyan-500/40 hover:to-cyan-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/10"
                  >
                    {saving ? 'Saving...' : 'Submit Rating'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}